import { ValidationErrors } from '@angular/forms';

type ValidationError = {
    Error: ErrorKey;
    Arguments: any;
};

export enum ValidatorKeys {
    required = 'required', // requiredTrue also returns this as an error
    max = 'max',
    maxExclusive = 'maxExclusive',
    min = 'min',
    minExclusive = 'minExclusive',
    duplicate = 'duplicate',
    password = 'password',
    email = 'email',
    match = 'match',
    url = 'url',
    regex = 'regex',
    exactlength = 'exactlength',
    exactDigit = 'exactDigit',
    minlength = 'minlength',
    maxlength = 'maxlength',
    api = 'api',
    subform = 'subform',
    maxFileSize = 'maxFileSize'
}

type ErrorKey = keyof typeof ValidatorKeys;
export type { ErrorKey };
export type ErrorMessages = Partial<AllErrorMessages>;

class AllErrorMessages {
    required = (value: any) => $localize`:@@CommonInputRequired:This field is required`;

    min = (value: { min: number; actual: number }) => $localize`:@@CommonInputValueTooSmallInclusive:Please enter an amount greater than or equal to ${value.min}`;

    minExclusive = (value: { min: number; actual: number }) => $localize`:@@CommonInputValueTooSmallExclusive:Please enter an amount greater than ${value.min}`;

    max = (value: { max: number; actual: number }) => $localize`:@@CommonInputValueTooLargeInclusive:Please enter an amount less than or equal to ${value.max}`;

    maxExclusive = (value: { max: number; actual: number }) => $localize`:@@CommonInputValueTooLargeExclusive:Please enter an amount less than ${value.max}`;

    duplicate = (value: string) => $localize`:@@CommonInputDuplicate:Please enter a unique value`;

    password = (value: { min: number; actual: number; score: number }) =>
        $localize`:@@CommonInputInvalidPassword:The password must be at least ${value.min} characters long, contain at least one number, one letter, and one special character`;

    email = (value: string) => $localize`:@@CommonInputInvalidEmail:Please enter a valid email address`;

    match = (value: string) => $localize`:@@CommonInputMismatch:Passwords do not match`;

    url = (value: string) => $localize`:@@CommonInputInvalidURL:Please enter a valid URL`;

    exactlength = (value: { requiredLength: number; actualLength: number }) =>
        $localize`:@@CommonInputStringWrongLength:Please enter exactly ${value.requiredLength} characters`;

    minlength = (value: { requiredLength: number; actualLength: number }) =>
        $localize`:@@CommonInputStringTooShort:Please enter at least ${value.requiredLength} characters`;

    maxlength = (value: { requiredLength: number; actualLength: number }) =>
        $localize`:@@CommonInputStringTooLong:Please enter at most ${value.requiredLength} characters`;

    exactDigit = (value: { requiredLength: number; actualLength: number }) =>
        $localize`:@@CommonInputNumberWrongLength:Please enter a ${value.requiredLength} digit number`;

    api = (value: any) => {
        throw new Error('API Error must be set by the developer using custom error messages');
        return '';
    };

    regex = (value: string) => {
        throw new Error('Regex Error must be set by the developer using custom error messages');
        return '';
    };

    subform = (value: any) => '';

    maxFileSize = (value: any) => {
        throw new Error('maxFileSize Error must be set by the developer using custom error messages');
        return '';
    };
}

export class Errors extends AllErrorMessages {
    Override(errors: ErrorMessages) {
        Object.assign(this, errors);
    }

    GetMessage(errors: ValidationErrors): string | null {
        if (!errors) {
            return null;
        }

        const error = FirstError(errors);

        return this[error.Error](error.Arguments);
    }
}

const ErrorPriorityOrder = Object.keys(ValidatorKeys) as ErrorKey[];

function FirstError(errors: ValidationErrors): ValidationError {
    // Get priorities of errors (lower index = higher priority)
    let priorities = Object.keys(errors).map(key => ErrorPriorityOrder.indexOf(key as ErrorKey));

    // Find index of highest priority error
    let index = Math.min(...priorities);

    let error = ErrorPriorityOrder[index];

    return { Error: error, Arguments: errors[error] };
}
